import React from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  MessagesSquare,
  BookUser,
  Settings,
  LogOut,
  ShieldCheck,
  ScrollText,
  BarChart3,
  Lightbulb,
  FileCheck2,
  FolderOpen,
  ClipboardList,
} from "lucide-react";
import { professionalTitle, userDisplayName, userInitials } from "@/lib/api";
import { APP_VERSION } from "@/lib/version";

const baseNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/messaggi", label: "Messaggi", icon: MessagesSquare, testid: "nav-messaggi" },
  { to: "/rubrica", label: "Rubrica", icon: BookUser, testid: "nav-rubrica" },
  { to: "/suggerimenti", label: "Suggerimenti", icon: Lightbulb, testid: "nav-idee" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, testid: "nav-impostazioni" },
];

const storicoNav = { to: "/storico-referti", label: "Storico referti", icon: FileCheck2, testid: "nav-storico-referti" };
const cartelleNav = { to: "/cartelle-pazienti", label: "Cartelle pazienti", icon: FolderOpen, testid: "nav-cartelle-pazienti" };
const casiNav = { to: "/casi", label: "Casi clinici", icon: ClipboardList, testid: "nav-casi-clinici" };

const navByProfession = {
  medico_specialista: [cartelleNav],
  medico_medicina_generale: [storicoNav, cartelleNav, casiNav],
  biologo_nutrizionista: [cartelleNav],
};

const adminNav = [
  { to: "/admin/statistiche", label: "Statistiche", icon: BarChart3, testid: "nav-admin-stats" },
  { to: "/admin/verifiche", label: "Verifica pro.", icon: ShieldCheck, testid: "nav-admin-verif" },
  { to: "/admin/audit", label: "Audit log", icon: ScrollText, testid: "nav-admin-audit" },
];

function NavItem({ to, label, icon: Icon, testid }) {
  return (
    <NavLink to={to} data-testid={testid} title={label}
      className={({ isActive }) =>
        `group relative flex flex-col items-center gap-1 rounded-md text-[11px] font-medium transition-colors
         px-1 py-2.5 justify-center text-center ${
          isActive
            ? "text-white bg-[#1E88E5]"
            : "text-slate-300 hover:text-white hover:bg-white/10"
        }`
      }>
      {({ isActive }) => (
        <>
          {/* Barra sinistra brand quando attivo */}
          <Icon className="h-5 w-5 flex-shrink-0" />
          <span className="hidden md:block leading-tight">{label}</span>
        </>
      )}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-[#EEF1F5] overflow-hidden">
      {/* Sidebar chiara */}
      <aside className="flex w-16 md:w-28 flex-shrink-0 bg-[#22303F]">
        <div className="h-full w-full flex flex-col">
          <div className="px-2 py-4 border-b border-white/10 flex items-center justify-center">
            <Link to="/dashboard" data-testid="sidebar-logo-link"
                  className="flex flex-col items-center group focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/40 rounded-lg">
              <img src="/icon.svg?v=20" alt="Synappsi"
                   className="h-10 w-10 mx-auto rounded-lg bg-white p-1 group-hover:opacity-90 transition-opacity" />
              <div className="hidden md:block text-center text-white font-heading font-bold text-sm mt-1.5 tracking-tight">Synappsi</div>
            </Link>
          </div>

          <nav className="flex-1 px-1.5 py-3 space-y-1 overflow-y-auto">
            {baseNav.map((item) => <NavItem key={item.to} {...item} />)}
            {(navByProfession[user?.profession] || []).map((item) => <NavItem key={item.to} {...item} />)}
            {user?.is_admin && (
              <>
                <div className="mt-4 px-2 pb-1 text-[9px] font-bold uppercase tracking-[0.24em] text-slate-400 text-center">
                  <span className="hidden md:inline">Admin</span>
                  <span className="md:hidden">•••</span>
                </div>
                {adminNav.map((item) => <NavItem key={item.to} {...item} />)}
              </>
            )}
          </nav>

          <div className="px-1.5 py-3 border-t border-white/10">
            <div className="flex flex-col items-center gap-1.5 px-1 py-2 mb-1 justify-center text-center"
                 title={userDisplayName(user)}>
              <div className="h-9 w-9 md:h-10 md:w-10 rounded-full overflow-hidden bg-[#1E88E5] text-white flex items-center justify-center font-heading font-bold flex-shrink-0 text-xs shadow-sm">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="h-full w-full object-cover" data-testid="sidebar-avatar" />
                ) : (
                  userInitials(user)
                )}
              </div>
              <div className="hidden md:block min-w-0 w-full">
                <div className="text-[11px] font-semibold text-white truncate" data-testid="sidebar-user-name">
                  {userDisplayName(user)}
                </div>
                <div className="text-[10px] text-slate-400 truncate">
                  {user?.is_admin ? "Amministratore" : professionalTitle(user?.profession || user?.role, user?.specialization)}
                </div>
              </div>
            </div>
            {/* Badge versione — mostrato sopra il bottone Esci */}
            <div className="hidden md:flex items-center justify-center gap-1.5 mb-1.5 px-2 py-1 rounded-md bg-white/5 border border-white/10"
                 data-testid="app-version-badge"
                 title={`Synappsi v${APP_VERSION}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
              <span className="text-[10px] font-mono font-semibold uppercase tracking-wider text-slate-300">
                v{APP_VERSION}
              </span>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-btn"
              title="Esci"
              className="w-full flex-col h-auto gap-1 justify-center text-slate-300 hover:text-white hover:bg-white/10 px-2 py-2 text-[11px]"
            >
              <LogOut className="h-5 w-5" />
              <span className="hidden md:inline">Esci</span>
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
